import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Controle segmentado do produto (o padrão que o Gantt inaugurou: trilho em
 * bg-secondary, opção ativa "elevada" em bg com texto primário e shadow-sm).
 *
 * Nasceu em 14/08/2026 porque o padrão estava re-implementado à mão em pelo
 * menos quatro lugares (Gantt viewMode, layout do Gantt, Portfólio
 * Lista/Heatmap, Equipe & Alocação). Regra da meugantt-ui: segmentado NUNCA se
 * re-implementa; novos usos importam daqui, e os quatro legados migram na fila
 * de adoção.
 */

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  icon?: LucideIcon;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /**
   * sm = 24px (toolbars densas) · md = 28px (padrão) · touch = 28px no desktop e
   * 44px abaixo de `md`.
   *
   * `touch` nasceu em 28/08/2026 para a página pública `/share/:token`, onde o
   * visitante não é usuário do produto, abre no telefone e não volta: ali o
   * alternador de visão é a navegação primária, e a escada do dedo do
   * DESIGN_SYSTEM.md pede 44px. É opt-in de propósito, para não mexer na altura
   * dos segmentados já existentes sem medir cada tela.
   */
  size?: 'sm' | 'md' | 'touch';
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      className={cn(
        'inline-flex rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-0.5',
        className
      )}
    >
      {options.map(opt => {
        const Icon = opt.icon;
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex items-center justify-center gap-1.5 rounded px-3 text-[13px] font-semibold transition-colors',
              size === 'sm' && 'h-6',
              size === 'md' && 'h-7',
              size === 'touch' && 'h-7 max-md:h-11',
              active
                ? 'bg-[var(--color-bg)] text-[var(--color-primary)] shadow-sm'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
            )}
          >
            {Icon && <Icon size={13} />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
